import {
  FormControl,
  FormLabel,
  HStack,
  Stack,
  Switch,
  Text,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { Heading, Input, Select } from '~/components';
import { DueDate, NonPaymentTypes } from '~/types/nonPayment';
import { isUUID } from '~/utils/uuid';

export interface AfterDueDateProps {
  depoisVenc?: DueDate;
  protesto?: DueDate;
  onChange: (obj: DueDate) => void;
}
export const AfterDueDate: React.VFC<AfterDueDateProps> = ({
  depoisVenc,
  protesto,
  onChange,
}) => {
  const { setError, formState, clearErrors } = useForm();
  const { errors } = formState;
  const depoisVenciInicial: DueDate = {
    enviar_email_em_dias: 1,
    repetir_email_em_dias: 0,
    limite_repeticao_email_em_dias: 0,
    status: false,
    tipo: NonPaymentTypes.DEPOIS_DO_VENCIMENTO,
    id: uuid(),
  };
  const protestoInicial: DueDate = {
    enviar_email_em_dias: 0,
    repetir_email_em_dias: 0,
    limite_repeticao_email_em_dias: 0,
    status: false,
    tipo: NonPaymentTypes.PROTESTO,
    id: uuid(),
  };
  const [valorDepoisVenc, setValorDepoisVenc] = useState<DueDate>(
    depoisVenc || depoisVenciInicial,
  );
  const [valorProtesto, setValorProtesto] = useState<DueDate>(
    protesto || protestoInicial,
  );

  useEffect(() => {
    setValorDepoisVenc(depoisVenc || depoisVenciInicial);
    setValorProtesto(protesto || protestoInicial);
  }, [depoisVenc, protesto]);

  const changeValores = (value: DueDate) => {
    if (value.tipo === NonPaymentTypes.DEPOIS_DO_VENCIMENTO) {
      clearErrors('enviar_email_em_dias_depois');
      if (value.enviar_email_em_dias! === 0) {
        setError('enviar_email_em_dias_depois', {
          type: 'manual',
          message: 'Informe um valor maior que 0',
        });
      }
      setValorDepoisVenc(value);
    } else {
      setValorProtesto(value);
    }
  };

  const diasAntesVencimento = () => {
    return (
      <>
        <HStack justifyContent="space-between">
          <Heading fontSize="16px">Depois do vencimento</Heading>
          <Switch
            id="status-depois"
            size="md"
            colorScheme="b"
            isChecked={valorDepoisVenc.status}
            onChange={e => {
              const aux = { ...valorDepoisVenc };
              aux.status = e.target.checked;
              changeValores(aux);
              onChange(aux);
            }}
          />
        </HStack>
        <FormControl isInvalid={!!errors} isRequired w="160px">
          <FormLabel mb={1} fontSize="14px" whiteSpace="nowrap">
            Enviar e-mail a cada
          </FormLabel>
          <HStack>
            <Input
              w="160px"
              name="enviar_email_em_dias_depois"
              type="text"
              error={errors?.enviar_email_em_dias_depois}
              value={valorDepoisVenc.enviar_email_em_dias}
              onChange={e => {
                const intValue = Number(e.target.value);
                if (Number.isNaN(intValue)) {
                  return;
                }
                const aux = { ...valorDepoisVenc };
                aux.enviar_email_em_dias = intValue;
                changeValores(aux);
              }}
              onBlur={() => {
                if (
                  valorDepoisVenc.enviar_email_em_dias !==
                    depoisVenc?.enviar_email_em_dias ||
                  isUUID(valorDepoisVenc.id!)
                ) {
                  onChange(valorDepoisVenc);
                }
              }}
            />
            <Text
              whiteSpace="nowrap"
              paddingBottom={!formState.isValid ? '49px' : ''}
            >
              dia(s)
            </Text>
          </HStack>
        </FormControl>
      </>
    );
  };

  const emailProtesto = () => {
    return (
      <>
        <HStack justifyContent="space-between">
          <Heading fontSize="16px">
            E-mail de Protesto e Contas a receber
          </Heading>
          <Switch
            id="status"
            size="md"
            colorScheme="b"
            isChecked={valorProtesto.status}
            onChange={e => {
              const aux = { ...valorProtesto };
              aux.status = e.target.checked;
              changeValores(aux);
              onChange(aux);
            }}
          />
        </HStack>
        <HStack flexWrap="wrap">
          <Text whiteSpace="nowrap">Enviar e-mail de protesto a partir de</Text>
          <FormControl
            isRequired
            isInvalid={errors?.enviar_email_em_dias_protesto}
            w="60px"
          >
            <Input
              name="enviar_email_em_dias_protesto"
              type="text"
              value={valorProtesto.enviar_email_em_dias}
              error={errors?.enviar_email_em_dias_protesto}
              onChange={e => {
                const intValue = Number(e.target.value);
                if (Number.isNaN(intValue)) {
                  return;
                }
                const aux = { ...valorProtesto };
                aux.enviar_email_em_dias = intValue;
                changeValores(aux);
              }}
              onBlur={() => {
                if (
                  valorProtesto.enviar_email_em_dias !==
                    protesto?.enviar_email_em_dias ||
                  isUUID(valorProtesto.id!)
                ) {
                  onChange(valorProtesto);
                }
              }}
            />
          </FormControl>
          <Text whiteSpace="nowrap">
            dia(s) após o vencimento. Repetir a mensagem a cada
          </Text>
          <FormControl
            isRequired
            isInvalid={errors?.repetir_email_em_dias}
            w="60px"
          >
            <Input
              name="repetir_email_em_dias"
              type="text"
              error={errors?.repetir_email_em_dias}
              value={valorProtesto.repetir_email_em_dias}
              onChange={e => {
                const intValue = Number(e.target.value);
                if (Number.isNaN(intValue)) {
                  return;
                }
                const aux = { ...valorProtesto };
                aux.repetir_email_em_dias = intValue;
                changeValores(aux);
              }}
              onBlur={() => {
                if (
                  valorProtesto.repetir_email_em_dias !==
                    protesto?.repetir_email_em_dias ||
                  isUUID(valorProtesto.id!)
                ) {
                  onChange(valorProtesto);
                }
              }}
            />
          </FormControl>
          <Text whiteSpace="nowrap">dia(s) por</Text>
          <FormControl
            isInvalid={errors?.limite_repeticao_email_em_dias}
            w="60px"
          >
            <Input
              name="limite_repeticao_email_em_dias"
              type="text"
              value={valorProtesto.limite_repeticao_email_em_dias}
              onChange={e => {
                const intValue = Number(e.target.value);
                if (Number.isNaN(intValue)) {
                  return;
                }
                const aux = { ...valorProtesto };
                aux.limite_repeticao_email_em_dias = intValue;
                changeValores(aux);
              }}
              onBlur={() => {
                if (
                  valorProtesto.limite_repeticao_email_em_dias !==
                    protesto?.limite_repeticao_email_em_dias ||
                  isUUID(valorProtesto.id!)
                ) {
                  onChange(valorProtesto);
                }
              }}
            />
          </FormControl>
          <Text whiteSpace="nowrap">dia(s).</Text>
        </HStack>
      </>
    );
  };

  return (
    <>
      <Stack pb={3} gridGap={3}>
        {diasAntesVencimento()}
      </Stack>
      <Stack gridGap={3}>{emailProtesto()}</Stack>
    </>
  );
};
